import { type ComponentType, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ClipStatus, ClipType } from '@air/api/types';
import { Skeleton } from '@air/component-skeleton';
import { ClipUtils } from '@air/utils-clips';

import { FileVisualizer } from '~/components/AssetModal/Visualizer/components/FileVisualizer';
import { MobileImageVisualizer } from '~/components/AssetModal/Visualizer/components/MobileImageVisualizer';
import { NoPreviewVisualizer } from '~/components/AssetModal/Visualizer/components/NoPreviewVisualizer';
import { useNoPreviewInVisualizer } from '~/hooks/useNoPreviewInVisualizer';
import {
  centralizedClipIsLoadingSelector,
  centralizedClipIsPDFTypeSelector,
  centralizedClipIsRiveSelector,
  centralizedClipPDFAssetSelector,
  centralizedClipStatusSelector,
  centralizedClipTypeSelector,
} from '~/store/centralizedClip/selectors';

enum VISUALIZER_TYPE {
  VIDEO = 'VIDEO',
  PDF = 'PDF',
  IMAGE = 'IMAGE',
  LOADER = 'LOADER',
  NOT_VIEWABLE = 'NOT_VIEWABLE',
  PROCESSING = 'PROCESSING',
  FILE = 'FILE',
  RIVE = 'RIVE',
}

export type MobileVisualizerProps = {
  VideoVisualizer?: ComponentType;
  PDFVisualizer: ComponentType;
  RiveVisualizer?: ComponentType;
};

export const MobileVisualizer = memo(({ VideoVisualizer, PDFVisualizer, RiveVisualizer }: MobileVisualizerProps) => {
  const isLoading = useSelector(centralizedClipIsLoadingSelector);
  const clipType = useSelector(centralizedClipTypeSelector);
  const clipStatus = useSelector(centralizedClipStatusSelector);
  const clipPDFAsset = useSelector(centralizedClipPDFAssetSelector);
  const isPdfRenderedFile = useSelector(centralizedClipIsPDFTypeSelector);
  const isRiveFile = useSelector(centralizedClipIsRiveSelector);
  const { noPreviewInVisualizer } = useNoPreviewInVisualizer();

  const visualizerType = useMemo<VISUALIZER_TYPE | null>(() => {
    if (isLoading) {
      return VISUALIZER_TYPE.LOADER;
    }

    if (!clipType) {
      return null;
    }

    if (isPdfRenderedFile) {
      if (clipPDFAsset) {
        return VISUALIZER_TYPE.PDF;
      } else {
        return VISUALIZER_TYPE.PROCESSING;
      }
    }

    // Check for nonMedia type before failed status (nonMedia takes precedence)
    if (clipType === ClipType.nonMedia) {
      return VISUALIZER_TYPE.FILE;
    }

    if (ClipUtils.isFailed(clipStatus)) {
      return VISUALIZER_TYPE.NOT_VIEWABLE;
    }

    /**
     * Show the processing state for all processing nonvideo assets
     * Show processing state for videos if video url isn't returned from websocket yet
     * When it is returned we know we have watchable video while the rest processes
     *  */
    if (noPreviewInVisualizer) {
      return VISUALIZER_TYPE.PROCESSING;
    }

    // Check for Rive files after processing check but BEFORE nonTranscodable check
    // since Rive files are marked as nonTranscodable but we can render them
    if (isRiveFile) {
      return VISUALIZER_TYPE.RIVE;
    }

    if (clipStatus === ClipStatus.nonTranscodable) {
      return VISUALIZER_TYPE.FILE;
    }

    if (clipType === ClipType.photo || clipType === ClipType.animated) {
      return VISUALIZER_TYPE.IMAGE;
    }

    return VISUALIZER_TYPE.VIDEO;
  }, [clipPDFAsset, clipStatus, clipType, isLoading, isPdfRenderedFile, isRiveFile, noPreviewInVisualizer]);

  const clipVisual = useMemo(() => {
    switch (visualizerType) {
      case VISUALIZER_TYPE.LOADER: {
        return <Skeleton width={300} />;
      }
      case VISUALIZER_TYPE.FILE: {
        return <FileVisualizer />;
      }
      case VISUALIZER_TYPE.NOT_VIEWABLE: {
        return <NoPreviewVisualizer />;
      }
      case VISUALIZER_TYPE.PDF: {
        return <PDFVisualizer />;
      }
      case VISUALIZER_TYPE.PROCESSING: {
        return <NoPreviewVisualizer isProcessing={true} />;
      }
      case VISUALIZER_TYPE.IMAGE: {
        return <MobileImageVisualizer />;
      }
      case VISUALIZER_TYPE.VIDEO: {
        return VideoVisualizer ? <VideoVisualizer /> : null;
      }
      case VISUALIZER_TYPE.RIVE: {
        return RiveVisualizer ? <RiveVisualizer /> : <FileVisualizer />;
      }
      default:
        return null;
    }
  }, [visualizerType, PDFVisualizer, VideoVisualizer, RiveVisualizer]);

  return (
    <div className="group/visualizer relative flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden p-0">
      <div className="flex size-full items-center justify-center">{clipVisual}</div>
    </div>
  );
});

MobileVisualizer.displayName = 'MobileVisualizer';
